import { useEffect, useMemo, useState } from "react";
import client from "../api/client";

const companyId = () => Number(localStorage.getItem("companyId") || 0);

const val = (r, ...keys) => {
  for (const k of keys) {
    const x = r?.[k];
    if (x !== undefined && x !== null) return x;
  }
  return "";
};

const safe = (v) => {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return "";
};

const arr = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.result)) return data.result;
  return [];
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const money = (v) =>
  num(v).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const errText = (e, fallback) => {
  const d = e?.response?.data;
  if (typeof d === "string" && d.trim()) return d;
  if (typeof d?.message === "string" && d.message.trim()) return d.message;
  if (typeof d?.title === "string" && d.title.trim()) return d.title;
  if (typeof e?.message === "string" && e.message.trim()) return e.message;
  return fallback;
};

const styles = {
  input: { height: 30, fontSize: 11, borderRadius: 7 },
  btn: { height: 30, fontSize: 11, borderRadius: 7, fontWeight: 700 },
  td: { fontSize: 11, padding: "6px 8px", whiteSpace: "nowrap" },
};

export default function EInvoicePage() {
  const [rows, setRows] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    invoiceId: "",
    invoiceNo: "",
    invoiceDate: "",
    customerId: "",
    customerName: "",
    gstin: "",
    transportMode: "ROAD",
    vehicleNo: "",
    distanceKM: "",
    taxableAmount: "",
    cgstAmount: "",
    sgstAmount: "",
    igstAmount: "",
    totalAmount: "",
  });

  const stats = useMemo(
    () => ({
      total: rows.length,
      generated: rows.filter(
        (x) =>
          safe(val(x, "EInvoiceStatus", "eInvoiceStatus")).toUpperCase() ===
          "GENERATED"
      ).length,
      cancelled: rows.filter(
        (x) =>
          safe(val(x, "EInvoiceStatus", "eInvoiceStatus")).toUpperCase() ===
          "CANCELLED"
      ).length,
      value: rows.reduce((s, x) => s + num(val(x, "TotalAmount", "totalAmount")), 0),
    }),
    [rows]
  );

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    await Promise.allSettled([loadRows(), loadInvoices()]);
    setLoading(false);
  };

  const loadRows = async () => {
    try {
      const res = await client.get("/e-invoices", {
        params: { companyId: companyId() || undefined },
      });

      setRows(arr(res.data));
    } catch (e) {
      console.error(e);
      setRows([]);
    }
  };

  const loadInvoices = async () => {
    try {
      const res = await client.get("/sales/invoices", {
        params: { companyId: companyId() || undefined },
      });

      setInvoices(arr(res.data));
    } catch (e) {
      console.error(e);
      setInvoices([]);
    }
  };

  const selectInvoice = (id) => {
    const inv = invoices.find(
      (x) => String(val(x, "Id", "id")) === String(id)
    );

    if (!inv) {
      setForm((f) => ({ ...f, invoiceId: "" }));
      return;
    }

    setForm({
      invoiceId: val(inv, "Id", "id"),
      invoiceNo: safe(val(inv, "InvoiceNo", "invoiceNo")),
      invoiceDate: safe(val(inv, "InvoiceDate", "invoiceDate")).slice(0, 10),
      customerId: val(inv, "CustomerId", "customerId"),
      customerName: safe(val(inv, "CustomerName", "customerName")),
      gstin: safe(val(inv, "GSTIN", "gstin", "Gstin")),
      transportMode: "ROAD",
      vehicleNo: "",
      distanceKM: "",
      taxableAmount: num(val(inv, "SubTotal", "subTotal")),
      cgstAmount: num(val(inv, "CGSTAmount", "cgstAmount")),
      sgstAmount: num(val(inv, "SGSTAmount", "sgstAmount")),
      igstAmount: num(val(inv, "IGSTAmount", "igstAmount")),
      totalAmount: num(val(inv, "GrandTotal", "grandTotal")),
    });
  };

  const generate = async () => {
    if (!form.invoiceId) return alert("Invoice required");
    if (form.transportMode === "ROAD" && safe(form.vehicleNo).trim() === "")
      return alert("Vehicle No is required for road transport");
    if (form.distanceKM && num(form.distanceKM) < 0)
      return alert("Distance KM cannot be negative");

    try {
      setSaving(true);

      await client.post("/e-invoices/generate", {
        companyId: companyId(),
        invoiceId: Number(form.invoiceId),
        invoiceNo: safe(form.invoiceNo),
        invoiceDate: form.invoiceDate,
        customerId: Number(form.customerId || 0),
        customerName: safe(form.customerName),
        gstin: safe(form.gstin),
        transportMode: safe(form.transportMode),
        vehicleNo: safe(form.vehicleNo).trim(),
        distanceKM: form.distanceKM === "" ? null : num(form.distanceKM),
        taxableAmount: num(form.taxableAmount),
        cgstAmount: num(form.cgstAmount),
        sgstAmount: num(form.sgstAmount),
        igstAmount: num(form.igstAmount),
        totalAmount: num(form.totalAmount),
      });

      alert("E-Invoice generated");

      setForm({
        invoiceId: "",
        invoiceNo: "",
        invoiceDate: "",
        customerId: "",
        customerName: "",
        gstin: "",
        transportMode: "ROAD",
        vehicleNo: "",
        distanceKM: "",
        taxableAmount: "",
        cgstAmount: "",
        sgstAmount: "",
        igstAmount: "",
        totalAmount: "",
      });

      await loadRows();
      await loadInvoices();
    } catch (e) {
      console.error(e);
      alert(errText(e, "Failed to generate e-invoice"));
    } finally {
      setSaving(false);
    }
  };

  const cancelInvoice = async (id) => {
    const reason = prompt("Enter cancellation reason");

    if (!reason || !reason.trim()) return;

    try {
      await client.post(`/e-invoices/cancel/${id}`, {
        companyId: companyId(),
        reason: reason.trim(),
      });

      alert("E-Invoice cancelled");

      await loadRows();
    } catch (e) {
      console.error(e);
      alert(errText(e, "Failed to cancel e-invoice"));
    }
  };

  return (
    <div className="container-fluid py-2" style={{ fontSize: 11, padding: 0 }}>
      <div
        style={{
          background: "#fff",
          borderRadius: 14,
          overflow: "hidden",
          boxShadow: "0 2px 10px rgba(0,0,0,.08)",
        }}
      >
        <div
          style={{
            background:
              "linear-gradient(90deg, rgb(11, 79, 74), rgb(15, 118, 110))",
            color: "#fff",
            padding: "9px 12px",
          }}
        >
          <h6 style={{ fontSize: 13, fontWeight: 800, margin: 0 }}>
            <i className="bi bi-file-earmark-code me-2"></i>
            E-Invoice Management
          </h6>

          <div style={{ fontSize: 10, opacity: 0.85 }}>
            GST IRN generation and compliance system
          </div>
        </div>

        <div style={{ padding: 10 }}>
          <div className="row g-2 mb-2">
            <Stat title="Generated" value={stats.generated} />
            <Stat title="Cancelled" value={stats.cancelled} />
            <Stat title="Total" value={stats.total} />
            <Stat title="Invoice Value" value={`₹ ${money(stats.value)}`} />
          </div>

          <Section title="Generate E-Invoice" />

          <div className="row g-2 mb-2">
            <Field label="Invoice">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={form.invoiceId}
                onChange={(e) => selectInvoice(e.target.value)}
              >
                <option value="">Select Invoice</option>

                {invoices.map((x, index) => {
                  const id = val(x, "Id", "id") || index;
                  return (
                    <option key={id} value={id}>
                      {safe(val(x, "InvoiceNo", "invoiceNo"))} -{" "}
                      {safe(val(x, "CustomerName", "customerName"))}
                    </option>
                  );
                })}
              </select>
            </Field>

            <Field label="Invoice Date">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={form.invoiceDate}
                readOnly
              />
            </Field>

            <Field label="Customer">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={form.customerName}
                readOnly
              />
            </Field>

            <Field label="GSTIN">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={form.gstin}
                readOnly
              />
            </Field>

            <Field label="Transport">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={form.transportMode}
                onChange={(e) =>
                  setForm({ ...form, transportMode: e.target.value })
                }
              >
                <option value="ROAD">ROAD</option>
                <option value="RAIL">RAIL</option>
                <option value="AIR">AIR</option>
                <option value="SHIP">SHIP</option>
              </select>
            </Field>

            <Field label="Vehicle No">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={form.vehicleNo}
                onChange={(e) =>
                  setForm({ ...form, vehicleNo: e.target.value.toUpperCase() })
                }
              />
            </Field>

            <Field label="Distance KM">
              <input
                type="number"
                min="0"
                className="form-control form-control-sm"
                style={styles.input}
                value={form.distanceKM}
                onChange={(e) =>
                  setForm({ ...form, distanceKM: e.target.value })
                }
              />
            </Field>

            <Field label="Total Amount">
              <input
                className="form-control form-control-sm text-end"
                style={styles.input}
                value={`₹ ${money(form.totalAmount)}`}
                readOnly
              />
            </Field>
          </div>

          <div className="d-flex justify-content-end gap-2 mb-3">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              style={styles.btn}
              onClick={() => window.print()}
            >
              Print
            </button>

            <button
              type="button"
              className="btn btn-success btn-sm"
              style={styles.btn}
              disabled={saving || loading}
              onClick={generate}
            >
              {saving ? "Generating..." : "Generate IRN"}
            </button>
          </div>

          <Section title="E-Invoice Register" />

          <div
            style={{
              maxHeight: 500,
              overflow: "auto",
              border: "1px solid #e9ecef",
              borderRadius: 10,
            }}
          >
            <table
              className="table table-bordered table-hover align-middle"
              style={{ fontSize: 11, minWidth: 1500, marginBottom: 0 }}
            >
              <thead>
                <tr>
                  {[
                    "Invoice",
                    "Date",
                    "Customer",
                    "GSTIN",
                    "IRN",
                    "Transport",
                    "Vehicle",
                    "Taxable",
                    "Total",
                    "Status",
                    "Action",
                  ].map((h) => (
                    <TH key={h}>{h}</TH>
                  ))}
                </tr>
              </thead>

              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="text-center text-muted" style={{ padding: 18 }}>
                      {loading ? "Loading..." : "No e-invoices found"}
                    </td>
                  </tr>
                ) : (
                  rows.map((r, index) => {
                    const status = safe(
                      val(r, "EInvoiceStatus", "eInvoiceStatus")
                    ).toUpperCase();

                    return (
                      <tr key={val(r, "Id", "id") || index}>
                        <td style={styles.td}>
                          {safe(val(r, "InvoiceNo", "invoiceNo"))}
                        </td>

                        <td style={styles.td}>
                          {safe(val(r, "InvoiceDate", "invoiceDate")).slice(0, 10)}
                        </td>

                        <td style={styles.td}>
                          {safe(val(r, "CustomerName", "customerName"))}
                        </td>

                        <td style={styles.td}>{safe(val(r, "GSTIN", "gstin"))}</td>

                        <td
                          style={{
                            ...styles.td,
                            fontSize: 10,
                            maxWidth: 280,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={safe(val(r, "IRN", "irn"))}
                        >
                          {safe(val(r, "IRN", "irn")) || "-"}
                        </td>

                        <td style={styles.td}>
                          {safe(val(r, "TransportMode", "transportMode"))}
                        </td>

                        <td style={styles.td}>
                          {safe(val(r, "VehicleNo", "vehicleNo")) || "-"}
                        </td>

                        <td className="text-end" style={styles.td}>
                          {money(val(r, "TaxableAmount", "taxableAmount"))}
                        </td>

                        <td className="text-end" style={styles.td}>
                          {money(val(r, "TotalAmount", "totalAmount"))}
                        </td>

                        <td style={styles.td}>
                          <span
                            className={`badge ${
                              status === "GENERATED"
                                ? "bg-success"
                                : status === "CANCELLED"
                                ? "bg-danger"
                                : "bg-secondary"
                            }`}
                          >
                            {status || "PENDING"}
                          </span>
                        </td>

                        <td style={styles.td}>
                          {status !== "CANCELLED" && (
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm"
                              style={styles.btn}
                              onClick={() => cancelInvoice(val(r, "Id", "id"))}
                            >
                              Cancel
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, col = "col-md-3" }) {
  return (
    <div className={col}>
      <label
        style={{
          fontSize: 10,
          fontWeight: 700,
          marginBottom: 3,
          color: "#495057",
        }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}

function Stat({ title, value }) {
  return (
    <div className="col-md-3">
      <div
        style={{
          border: "1px solid #e9ecef",
          background: "#f8fafc",
          borderRadius: 10,
          padding: 9,
          borderTop: "3px solid #0f766e",
        }}
      >
        <div style={{ fontSize: 10, color: "#64748b", fontWeight: 800 }}>
          {title}
        </div>

        <div style={{ fontSize: 15, color: "#0f766e", fontWeight: 900 }}>
          {safe(value)}
        </div>
      </div>
    </div>
  );
}

function Section({ title }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 800,
        color: "#0f766e",
        borderBottom: "1px solid #e9ecef",
        paddingBottom: 4,
        marginBottom: 8,
      }}
    >
      {title}
    </div>
  );
}

function TH({ children }) {
  return (
    <th
      style={{
        fontSize: 10,
        color: "#fff",
        background: "#07111f",
        padding: "7px 8px",
        whiteSpace: "nowrap",
        position: "sticky",
        top: 0,
      }}
    >
      {children}
    </th>
  );
}